/**
 * supabase.js — hardened client for production
 *
 * Security decisions:
 *  - Singleton: one client instance, never recreated per render
 *  - Realtime disabled: resume builder has zero realtime needs;
 *    disabling it saves the free-tier 2-connection limit for the forum
 *  - Auth storage: sessionStorage (tab-scoped) prevents stale tokens
 *    persisting across browser sessions in shared/public devices
 *  - detectSessionInUrl: false — we handle magic-link tokens manually
 *    via our own HMAC verification, not Supabase's built-in flow
 *  - No VITE_SUPABASE_* keys are ever logged or exposed to the console
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://qxsardezvxsquvejvsso.supabase.co';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// ── Singleton client ──────────────────────────────────────────────────────────

export const supabase = (() => {
  try {
    if (!supabaseUrl || !supabaseKey) {
      console.warn('Supabase configuration missing — check .env.local');
      return null;
    }
    return createClient(supabaseUrl, supabaseKey, {
      auth: {
        // sessionStorage: cleared when tab closes — prevents stale auth on shared devices
        storage:              typeof window !== 'undefined' ? window.sessionStorage : undefined,
        persistSession:       true,
        autoRefreshToken:     true,
        // We verify magic-link tokens ourselves via HMAC — disable Supabase's URL handler
        detectSessionInUrl:   false,
        // Suppress Supabase's own auth state broadcasts to avoid stale cache issues
        flowType:             'pkce',
      },
      realtime: {
        // Enabled for forum realtime sync
        params: { eventsPerSecond: 5 },
      },
      global: {
        headers: {
          // Identify requests in Supabase logs without exposing secrets
          'x-app-name': 'testingvala-resume-builder',
        },
      },
    });
  } catch (error) {
    console.error('Supabase initialization failed:', error.message);
    return null;
  }
})();

// ── Environment flags ─────────────────────────────────────────────────────────

export const ENV = {
  isProduction: import.meta.env.VITE_APP_ENV === 'production' ||
                import.meta.env.VERCEL_ENV === 'production',
  isDevelopment: import.meta.env.VITE_APP_ENV === 'development' ||
                 import.meta.env.DEV === true,
};

// ── Resume download analytics ─────────────────────────────────────────────────
// This is the ONLY write the resume builder makes to Supabase.
// No resume content, no PDF, no form data — only the minimal analytics row.

/**
 * trackResumeDownload({ email, specialization, atsScore })
 *
 * Fire-and-forget. Never throws — a failed analytics write must never
 * block the user's PDF download.
 */
export async function trackResumeDownload({ email, specialization, atsScore }) {
  // Intentionally disabled to conserve Supabase free-tier limits as requested by the user.
  // This prevents 'PGRST205' schema cache errors for non-existent tables.
  return;
}

// ── Combined subscription + config check (single RPC, replaces 2 queries) ────

/**
 * checkResumeAccess(email) → { active, pending, config }
 *
 * Calls the check_resume_access RPC which combines:
 *   1. premium_users lookup
 *   2. payment_requests lookup
 *   3. payment_config fetch
 * into a single database round-trip.
 *
 * Returns safe defaults on any error so the UI never crashes.
 */
export async function checkResumeAccess(email) {
  const DEFAULT = { active: false, pending: false, config: { monthly_price: 99, upi_id: 'testingvala@paytm' } };

  if (!email?.trim()) return DEFAULT;

  try {
    const { data, error } = await supabase.rpc('check_resume_access', {
      p_email: email.toLowerCase().trim(),
    });

    if (error || !data) return DEFAULT;
    return {
      active:  Boolean(data.active),
      pending: Boolean(data.pending),
      config:  data.config || DEFAULT.config,
    };
  } catch {
    return DEFAULT;
  }
}

export const TABLES = {
  USER_PROFILES:       'user_profiles',
  FORUM_CATEGORIES:    'forum_categories',
  FORUM_POSTS:         'forum_posts',
  POST_LIKES:          'post_likes',
  POST_COMMENTS:       'post_comments',
  WEBSITE_CONTENT:     'website_content',
  WEBINARS:            'webinars',          // canonical — single production webinar table
  CONTEST_SUBMISSIONS: 'contest_submissions'
};

export default supabase;
